import React, { useState, useEffect, useCallback } from 'react';
import './CareparrvaOutput.css';
import careparrvaOutputService from '../../services/careparrvaOutputService';
import { showToast } from '../../components/toast';
import CareparrvaReportViewer from './CareparrvaReportViewer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSyncAlt,
  faDownload,
  faSpinner,
  faExclamationTriangle,
  faChartLine,
  faBriefcase,
  faClock,
  faLayerGroup,
  faCogs,
  faRobot,
  faListAlt,
  faCheckCircle,
  faSlidersH,
  faDatabase,
  faSave,
  faEye,
  faTrash,
  faArrowLeft,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';

// Report Type Tabs Definition with valid CareEdge PaRRVA category defaults
const REPORT_TABS = [
  { value: 'SINGLE_STOCK', label: 'Single Stock', icon: faChartLine, defaultCategory: 'SUMMARY' },
  { value: 'PORTFOLIO', label: 'Portfolio', icon: faBriefcase, defaultCategory: 'DETAIL' },
  { value: 'INTRADAY', label: 'Intraday', icon: faClock, defaultCategory: 'SUMMARY' },
  { value: 'DERIVATIVE', label: 'Derivatives', icon: faLayerGroup, defaultCategory: 'SUMMARY' },
  // { value: 'STRATEGY', label: 'Strategy', icon: faCogs, defaultCategory: 'SUMMARY' },
  // { value: 'ALGORITHM', label: 'Algorithm', icon: faRobot, defaultCategory: 'SUMMARY' },
  { value: 'ALL', label: 'All Report Advice', icon: faListAlt, defaultCategory: 'SUMMARY' },
];

// Available options for top bar controls
const DATA_TYPES = [
  { value: 'LATEST_DATA', label: 'Latest Data (Current Session)' },
  { value: 'PREV_MONTH_END_DATA', label: 'Previous Month-End Data' },
];

const REPORT_CATEGORIES = [
  { value: 'DETAIL', label: 'Detail' },
  { value: 'SUMMARY', label: 'Summary' },
];

const REPORT_FORMATS = [
  { value: 'DATA_POINTS', label: 'Raw Data Points (JSON)' },
  { value: 'PDF', label: 'PDF Document' },
  { value: 'PNG', label: 'PNG Image' },
  { value: 'JPEG', label: 'JPEG Image' },
  { value: 'QR', label: 'QR Code' },
  { value: 'URL', label: 'Direct Web URL' },
];

const CareparrvaOutput = () => {
  // Navigation View Mode: 'live' (tabs) or 'saved_reports' (history table)
  const [viewMode, setViewMode] = useState('live');

  // Active Tab state (default: SINGLE_STOCK per user specification)
  const [activeTab, setActiveTab] = useState('SINGLE_STOCK');

  // Top Bar Options state with default values
  const [options, setOptions] = useState({
    data_type: 'LATEST_DATA',
    report_category: 'SUMMARY',
    report_format: 'DATA_POINTS',
    is_historical: 'FALSE',
  });

  // Report Data & Loading State
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  // Saved Reports History State
  const [savedReports, setSavedReports] = useState([]);
  const [savedTotal, setSavedTotal] = useState(0);
  const [savedPage, setSavedPage] = useState(1);
  const [savedLimit] = useState(10);
  const [savedSearch, setSavedSearch] = useState('');
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [selectedSavedReport, setSelectedSavedReport] = useState(null);

  // Helper to extract clean error message
  const extractErrorMessage = (err, fallback = 'Failed to fetch report data.') => {
    const data = err?.response?.data || err;
    const messages = data?.rawResponse?.messages || data?.messages;
    if (Array.isArray(messages) && messages.length > 0) {
      const details = messages
        .map((m) => {
          if (m?.errcode === 'invalid_json') {
            return 'Invalid parameters received by CareEdge PaRRVA API for this report type.';
          }
          if (m?.errcode === 'not_exist') {
            return 'No enrolled active report data found on CareEdge PaRRVA for this category.';
          }
          if (m?.errcode === 'cooldown_period') {
            const val = Array.isArray(m?.vals) && m.vals.length > 0 ? m.vals.join(', ') : '7 days';
            return `Cooldown active: successive report generations require ${val}.`;
          }
          const valText = Array.isArray(m?.vals) && m.vals.length > 0 ? m.vals.join(', ') : '';
          const field = m?.field ? `[${m.field}] ` : '';
          return valText ? `${field}${valText}` : m?.message || m?.errcode || '';
        })
        .filter(Boolean);
      if (details.length > 0) return details.join(' | ');
    }
    if (data?.message) {
      return Array.isArray(data.message) ? data.message.join(', ') : data.message;
    }
    return err?.message || fallback;
  };

  // Fetch count of saved reports from database
  const fetchSavedReportsCount = useCallback(async () => {
    try {
      const res = await careparrvaOutputService.getReports({ limit: 1 });
      if (res && res.total !== undefined) {
        setSavedTotal(res.total);
      }
    } catch (e) {
      console.error('Error getting saved count:', e);
    }
  }, []);

  // Fetch list of saved reports from database
  const fetchSavedReportsList = useCallback(
    async (page = 1, search = '') => {
      try {
        setLoadingSaved(true);
        const res = await careparrvaOutputService.getReports({
          page,
          limit: savedLimit,
          search,
        });
        if (res && res.data) {
          setSavedReports(res.data);
          setSavedTotal(res.total || 0);
          setSavedPage(res.page || page);
        }
      } catch (e) {
        console.error('Error getting saved reports list:', e);
        showToast.error(extractErrorMessage(e, 'Failed to load saved reports.'));
      } finally {
        setLoadingSaved(false);
      }
    },
    [savedLimit]
  );

  // Fetch live tab report data without storing into DB
  const fetchTabData = useCallback(
    async (tabType, currentOptions = options) => {
      try {
        setLoading(true);
        setErrorMsg(null);

        const payload = {
          data: {
            data_type: currentOptions.data_type || 'LATEST_DATA',
            report_type: tabType || 'SINGLE_STOCK',
            report_category: currentOptions.report_category || 'SUMMARY',
            report_format: currentOptions.report_format || 'DATA_POINTS',
            is_historical: currentOptions.is_historical || 'FALSE',
          },
        };

        const res = await careparrvaOutputService.getTabReport(payload);

        if (res && (res.success || res.report)) {
          setReportData(res.report || res);
          setLastFetched(new Date());
        } else {
          setReportData(null);
          const err = extractErrorMessage(res, 'No report data returned from server.');
          setErrorMsg(err);
        }
      } catch (err) {
        console.error('Error fetching tab data:', err);
        const errStr = extractErrorMessage(err);
        setErrorMsg(errStr);
        setReportData(null);
      } finally {
        setLoading(false);
      }
    },
    [options]
  );

  // Initial load on component mount
  useEffect(() => {
    fetchTabData(activeTab, options);
    fetchSavedReportsCount();
  }, []);

  // Tab switch handler: fetches live data or switches to Saved Reports tab
  const handleTabClick = (tabKey) => {
    if (tabKey === 'SAVED_REPORTS') {
      setActiveTab('SAVED_REPORTS');
      setSelectedSavedReport(null);
      fetchSavedReportsList(1, '');
      return;
    }

    if (tabKey === activeTab && !loading) return;
    const tabObj = REPORT_TABS.find((t) => t.value === tabKey);
    const targetCategory = tabObj ? tabObj.defaultCategory : 'SUMMARY';

    const updatedOptions = {
      ...options,
      report_category: targetCategory,
    };

    setActiveTab(tabKey);
    setSelectedSavedReport(null);
    setOptions(updatedOptions);
    fetchTabData(tabKey, updatedOptions);
  };

  // Top Bar Option Change handler: always fetches live data with updated options
  const handleOptionChange = (field, value) => {
    const updatedOptions = {
      ...options,
      [field]: value,
    };
    setOptions(updatedOptions);
    fetchTabData(activeTab, updatedOptions);
  };

  // Force Refresh Live Data
  const handleForceRefresh = () => {
    fetchTabData(activeTab, options);
  };

  // Explicit Save to Database Handler
  const handleSaveToDb = async () => {
    if (!reportData) return;
    try {
      setSaving(true);
      const res = await careparrvaOutputService.saveReport({
        report: reportData,
      });
      if (res && (res.success || res.report)) {
        showToast.success(res.message || 'Report saved to database successfully!');
        setReportData((prev) => ({
          ...prev,
          ...(res.report || {}),
          is_saved: true,
          id: res.report?.id || prev?.id,
        }));
        fetchSavedReportsCount();
      } else {
        showToast.error(res?.message || 'Could not save report');
      }
    } catch (err) {
      console.error('Error saving report to DB:', err);
      showToast.error(extractErrorMessage(err, 'Failed to save report to database.'));
    } finally {
      setSaving(false);
    }
  };

  // Delete a saved report from database
  const handleDeleteSavedReport = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete Saved Report #${id}?`)) {
      return;
    }
    try {
      await careparrvaOutputService.deleteReport(id);
      showToast.success(`Report #${id} deleted from database successfully!`);
      if (reportData?.id === id) {
        setReportData((prev) => ({ ...prev, is_saved: false, id: null }));
      }
      if (selectedSavedReport?.id === id) {
        setSelectedSavedReport(null);
      }
      fetchSavedReportsList(savedPage, savedSearch);
      fetchSavedReportsCount();
    } catch (err) {
      console.error('Error deleting report:', err);
      showToast.error(extractErrorMessage(err, 'Failed to delete report.'));
    }
  };

  // Switch to Saved Reports view
  const handleOpenSavedReports = () => {
    setViewMode('saved_reports');
    setSelectedSavedReport(null);
    fetchSavedReportsList(1, '');
  };

  // Sync Pending Status
  const handleSyncStatus = async () => {
    const repId = reportData?.id || selectedSavedReport?.id;
    if (!repId) return;
    try {
      setSyncing(true);
      const res = await careparrvaOutputService.syncReportStatus(repId);
      if (res && (res.report || res.data || res.success)) {
        const updated = res.report || res.data;
        if (viewMode === 'live') {
          setReportData(updated || reportData);
        } else {
          setSelectedSavedReport(updated || selectedSavedReport);
          fetchSavedReportsList(savedPage, savedSearch);
        }
        showToast.success('Report status checked and refreshed!');
      } else {
        showToast.info(res?.message || 'Report is still being generated by CareEdge PaRRVA');
      }
    } catch (err) {
      console.error('Error syncing status:', err);
      showToast.error(extractErrorMessage(err, 'Failed to sync status.'));
    } finally {
      setSyncing(false);
    }
  };

  // Download raw JSON
  const handleDownloadJson = (dataToDownload = null) => {
    const target = dataToDownload || reportData || selectedSavedReport;
    if (!target?.data_points) {
      showToast.warning('No data points available to download');
      return;
    }
    try {
      const content =
        typeof target.data_points === 'string'
          ? target.data_points
          : JSON.stringify(target.data_points, null, 2);
      const blob = new Blob([content], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `careparrva_${(target.report_type || 'report').toLowerCase()}_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast.success('JSON file downloaded successfully');
    } catch (err) {
      console.error('Error downloading JSON:', err);
      showToast.error('Could not download JSON');
    }
  };

  const activeTabMeta =
    REPORT_TABS.find((t) => t.value === activeTab) ||
    (activeTab === 'SAVED_REPORTS'
      ? { value: 'SAVED_REPORTS', label: 'Saved Reports', icon: faDatabase }
      : REPORT_TABS[0]);

  return (
    <div id="page-container" className="page-header-fixed page-sidebar-fixed fade in cpo-container">
      <div id="content" className="p-3">
        {/* ============================================================== */}
        {/* TOP HEADER: Breadcrumbs & Primary Actions                     */}
        {/* ============================================================== */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 pb-2 mb-2">
          <div>
            <ul className="breadcrumb mb-0">
              <li className="breadcrumb-item">
                <a href="/Dashboard">
                  <i className="ti-home"></i> Home
                </a>
              </li>
              <li className="breadcrumb-item">Research</li>
              <li className="breadcrumb-item active">
                Careparrva Output {activeTab === 'SAVED_REPORTS' ? '— Saved Reports' : ''}
              </li>
            </ul>
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap">
            {activeTab !== 'SAVED_REPORTS' ? (
              <>
                {/* Single Save Button for the currently active tab (Commented out) */}
                {/* {reportData && (
                  <button
                    type="button"
                    className={`btn btn-sm font-weight-bold ${
                      reportData.is_saved ? 'cpo-btn-saved' : 'btn-success'
                    }`}
                    onClick={handleSaveToDb}
                    disabled={saving || reportData.is_saved}
                    title={
                      reportData.is_saved
                        ? `${activeTabMeta.label} report is saved in database`
                        : `Save ${activeTabMeta.label} to database`
                    }
                  >
                    <FontAwesomeIcon
                      icon={saving ? faSpinner : reportData.is_saved ? faCheckCircle : faSave}
                      spin={saving}
                      className="me-1"
                    />
                    {saving
                      ? 'Saving...'
                      : reportData.is_saved
                      ? 'Saved in Database'
                      : 'Save to Database'}
                  </button>
                )} */}

                {/* Download JSON Button */}
                {reportData?.data_points && (
                  <button
                    type="button"
                    className="btn btn-default btn-sm font-weight-bold"
                    onClick={() => handleDownloadJson()}
                    disabled={loading}
                    title="Download report data as JSON file"
                  >
                    <FontAwesomeIcon icon={faDownload} className="me-1 text-primary" /> Download JSON
                  </button>
                )}

                {/* Fetch Live / Refresh Button */}
                <button
                  type="button"
                  className="btn btn-primary btn-sm font-weight-bold"
                  onClick={handleForceRefresh}
                  disabled={loading}
                  title="Directly fetch fresh live data from Careparrva API"
                >
                  <FontAwesomeIcon icon={faSyncAlt} spin={loading} className="me-1" />{' '}
                  {loading ? 'Fetching...' : 'Fetch Live / Refresh'}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-primary btn-sm font-weight-bold"
                  onClick={() => handleTabClick('SINGLE_STOCK')}
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="me-1" /> Back to Live Reports
                </button>
              </>
            )}
          </div>
        </div>

        {/* ============================================================== */}
        {/* REPORT TYPE TABS (INCLUDES SAVED REPORTS TAB)                  */}
        {/* ============================================================== */}
        <div className="cpo-nav-tabs-wrapper">
          <div className="cpo-nav-tabs">
            {REPORT_TABS.map((tab) => {
              const isActive = activeTab === tab.value;
              return (
                <button
                  key={tab.value}
                  type="button"
                  className={`cpo-nav-tab ${isActive ? 'active' : ''}`}
                  onClick={() => handleTabClick(tab.value)}
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={tab.icon} className="cpo-tab-icon" />
                  <span>{tab.label}</span>
                </button>
              );
            })}

            {/* Saved Reports Tab (Commented out) */}
            {/* <button
              type="button"
              className={`cpo-nav-tab ${activeTab === 'SAVED_REPORTS' ? 'active' : ''}`}
              onClick={() => handleTabClick('SAVED_REPORTS')}
            >
              <FontAwesomeIcon icon={faDatabase} className="cpo-tab-icon text-info" />
              <span>Saved Reports</span>
              <span
                className="badge badge-info ms-1"
                style={{
                  backgroundColor: activeTab === 'SAVED_REPORTS' ? '#0284c7' : '#e0f2fe',
                  color: activeTab === 'SAVED_REPORTS' ? '#ffffff' : '#0369a1',
                  fontSize: '11px',
                  padding: '2px 7px',
                  borderRadius: '10px',
                }}
              >
                {savedTotal}
              </span>
            </button> */}
          </div>
        </div>

        {/* ============================================================== */}
        {/* VIEW 1: LIVE TABS VIEW (DIRECT API FETCH, NO AUTO-SAVE)        */}
        {/* ============================================================== */}
        {activeTab !== 'SAVED_REPORTS' && (
          <>
            {/* Global Options Bar (Above Tabs Content) */}
            <div className="cpo-options-card">
              <div className="cpo-options-header">
                <div className="cpo-options-title">
                  <FontAwesomeIcon icon={faSlidersH} className="text-primary me-1" />
                  <span>Report Parameters</span>
                  <span className="text-muted font-weight-normal ml-1" style={{ fontSize: '11.5px' }}>
                    (Auto-applied on tab switch)
                  </span>
                </div>
                <div className="d-flex align-items-center gap-2" style={{ fontSize: '11.5px' }}>
                  {/* {reportData?.is_saved ? (
                    <span className="badge cpo-badge-cache">
                      <FontAwesomeIcon icon={faCheckCircle} className="me-1 text-success" /> Saved in Database
                    </span>
                  ) : (
                    <span className="badge cpo-badge-live">
                      <FontAwesomeIcon icon={faSyncAlt} className="me-1" /> Live Direct API (Unsaved)
                    </span>
                  )} */}
                  {lastFetched && (
                    <span className="text-muted ml-2">
                      Fetched:{' '}
                      <strong>
                        {lastFetched.toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                        })}
                      </strong>
                    </span>
                  )}
                </div>
              </div>

              <div className="cpo-options-grid">
                {/* Data Type */}
                <div className="cpo-form-group">
                  <label className="cpo-label">Data Type</label>
                  <select
                    className="form-control cpo-select"
                    value={options.data_type}
                    onChange={(e) => handleOptionChange('data_type', e.target.value)}
                    disabled={loading}
                  >
                    {DATA_TYPES.map((d) => (
                      <option key={d.value} value={d.value}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Report Category */}
                <div className="cpo-form-group">
                  <label className="cpo-label">Category</label>
                  <select
                    className="form-control cpo-select"
                    value={options.report_category}
                    onChange={(e) => handleOptionChange('report_category', e.target.value)}
                    disabled={loading}
                  >
                    {REPORT_CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Report Format */}
                <div className="cpo-form-group">
                  <label className="cpo-label">Format</label>
                  <select
                    className="form-control cpo-select"
                    value={options.report_format}
                    onChange={(e) => handleOptionChange('report_format', e.target.value)}
                    disabled={loading}
                  >
                    {REPORT_FORMATS.map((f) => (
                      <option key={f.value} value={f.value}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Historical Mode */}
                <div className="cpo-form-group">
                  <label className="cpo-label">Historical Mode</label>
                  <select
                    className="form-control cpo-select"
                    value={options.is_historical}
                    onChange={(e) => handleOptionChange('is_historical', e.target.value)}
                    disabled={loading}
                  >
                    <option value="FALSE">No (Current Session)</option>
                    <option value="TRUE">Yes (Historical Queue)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Direct Data Display Card */}
            <div className="cpo-display-card">
              <div className="cpo-display-header">
                <div className="d-flex align-items-center gap-2">
                  <FontAwesomeIcon icon={activeTabMeta.icon} className="text-primary" />
                  <h5 className="m-0 font-weight-bold" style={{ fontSize: '15px', color: '#0f172a' }}>
                    {activeTabMeta.label} Report
                  </h5>
                  <span
                    className="badge badge-default ml-2"
                    style={{ backgroundColor: '#f1f5f9', color: '#475569' }}
                  >
                    {options.report_format}
                  </span>
                </div>
                {reportData && (
                  <div className="d-flex align-items-center gap-2 text-muted" style={{ fontSize: '12px' }}>
                    {reportData.is_saved ? (
                      <span className="badge badge-success px-2 py-1">
                        <FontAwesomeIcon icon={faCheckCircle} className="me-1" /> Saved in DB (ID: {reportData.id})
                      </span>
                    ) : (
                      <span className="badge badge-warning text-dark px-2 py-1">
                        Live API Data (Not Saved)
                      </span>
                    )}
                    <span>Category: <strong>{reportData.report_category}</strong></span>
                  </div>
                )}
              </div>

              <div className="cpo-display-body">
                {/* Loading Indicator */}
                {loading && (
                  <div className="text-center py-5">
                    <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-primary mb-3" />
                    <div className="font-weight-bold" style={{ fontSize: '14px', color: '#1e293b' }}>
                      Fetching {activeTabMeta.label} Live Data...
                    </div>
                    <div className="text-muted" style={{ fontSize: '12px', marginTop: '4px' }}>
                      Calling CareEdge PaRRVA API directly without saving to database
                    </div>
                  </div>
                )}

                {/* Error Alert Box */}
                {!loading && errorMsg && (
                  <div className="alert alert-danger p-3 mb-0" style={{ borderRadius: '6px' }}>
                    <div className="d-flex align-items-start">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger mt-1 mr-2" />
                      <div style={{ flex: 1 }}>
                        <strong className="text-danger">Failed to Load Report:</strong>
                        <div style={{ marginTop: '4px', wordBreak: 'break-word', fontSize: '13px' }}>
                          {errorMsg}
                        </div>
                        <div className="mt-3">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => fetchTabData(activeTab, options)}
                          >
                            <FontAwesomeIcon icon={faSyncAlt} className="mr-1" /> Try Again
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger ml-2"
                            onClick={handleForceRefresh}
                          >
                            Force Fetch from API
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Data Report Viewer */}
                {!loading && !errorMsg && reportData && (
                  <CareparrvaReportViewer
                    report={reportData}
                    onSync={handleSyncStatus}
                    syncing={syncing}
                    onDownloadJson={() => handleDownloadJson(reportData)}
                  />
                )}

                {/* Empty fallback */}
                {!loading && !errorMsg && !reportData && (
                  <div className="text-center py-5 text-muted">
                    <p>No data available for this report type.</p>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={handleForceRefresh}
                    >
                      Fetch Live Data
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* ============================================================== */}
        {/* VIEW 2: SAVED REPORTS TABLE & DETAIL VIEW                      */}
        {/* ============================================================== */}
        {activeTab === 'SAVED_REPORTS' && (
          <div className="cpo-saved-card">
            {/* If user clicked View on a specific saved report */}
            {selectedSavedReport ? (
              <div>
                <div className="cpo-saved-toolbar">
                  <div className="d-flex align-items-center gap-3">
                    <button
                      type="button"
                      className="btn btn-default btn-sm font-weight-bold"
                      onClick={() => setSelectedSavedReport(null)}
                    >
                      <FontAwesomeIcon icon={faArrowLeft} className="me-1" /> Back to Saved Reports Table
                    </button>
                    <div>
                      <strong style={{ fontSize: '15px', color: '#0f172a' }}>
                        Report #{selectedSavedReport.id} — {selectedSavedReport.report_type} ({selectedSavedReport.report_format})
                      </strong>
                      <span className="text-muted ms-2" style={{ fontSize: '12px' }}>
                        Saved: {new Date(selectedSavedReport.created_on).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <div>
                    {selectedSavedReport.data_points && (
                      <button
                        type="button"
                        className="btn btn-default btn-sm font-weight-bold me-2"
                        onClick={() => handleDownloadJson(selectedSavedReport)}
                      >
                        <FontAwesomeIcon icon={faDownload} className="me-1 text-primary" /> Download JSON
                      </button>
                    )}
                  </div>
                </div>

                <div className="p-3">
                  <CareparrvaReportViewer
                    report={selectedSavedReport}
                    onSync={handleSyncStatus}
                    syncing={syncing}
                    onDownloadJson={() => handleDownloadJson(selectedSavedReport)}
                  />
                </div>
              </div>
            ) : (
              <div>
                {/* Saved Reports Table Toolbar */}
                <div className="cpo-saved-toolbar">
                  <div className="d-flex align-items-center gap-2">
                    <FontAwesomeIcon icon={faDatabase} className="text-primary" />
                    <h5 className="m-0 font-weight-bold" style={{ fontSize: '15px', color: '#0f172a' }}>
                      Database Saved Reports
                    </h5>
                    <span className="badge badge-primary" style={{ backgroundColor: '#0284c7' }}>
                      {savedTotal} Total Records
                    </span>
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    <div className="input-group" style={{ width: '260px' }}>
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search report type, format..."
                        value={savedSearch}
                        onChange={(e) => {
                          setSavedSearch(e.target.value);
                          fetchSavedReportsList(1, e.target.value);
                        }}
                        style={{ height: '34px', fontSize: '12.5px' }}
                      />
                      <span className="input-group-addon bg-white">
                        <FontAwesomeIcon icon={faSearch} className="text-muted" />
                      </span>
                    </div>

                    <button
                      type="button"
                      className="btn btn-default btn-sm"
                      onClick={() => fetchSavedReportsList(savedPage, savedSearch)}
                      title="Refresh Saved Reports List"
                    >
                      <FontAwesomeIcon icon={faSyncAlt} spin={loadingSaved} />
                    </button>
                  </div>
                </div>

                {/* Table container */}
                <div className="table-responsive">
                  <table className="cpo-saved-table">
                    <thead>
                      <tr>
                        <th style={{ width: '60px' }}># ID</th>
                        <th>Report Type</th>
                        <th>Category</th>
                        <th>Format</th>
                        <th>Data Type</th>
                        <th className="text-center">Historical</th>
                        <th className="text-center">Status</th>
                        <th>Saved On</th>
                        <th className="text-center" style={{ width: '130px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingSaved ? (
                        <tr>
                          <td colSpan="9" className="text-center py-5">
                            <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-primary mb-2" />
                            <div className="text-muted">Loading saved reports from database...</div>
                          </td>
                        </tr>
                      ) : savedReports.length === 0 ? (
                        <tr>
                          <td colSpan="9" className="text-center py-5 text-muted">
                            <FontAwesomeIcon icon={faDatabase} size="2x" className="mb-2 text-muted" />
                            <div>No reports have been saved in the database yet.</div>
                            <div style={{ fontSize: '12px' }} className="mt-1">
                              Switch to <strong>Live Tabs</strong> and click <strong>"Save to Database"</strong> to store any report here.
                            </div>
                          </td>
                        </tr>
                      ) : (
                        savedReports.map((row) => (
                          <tr key={row.id}>
                            <td className="font-weight-bold">#{row.id}</td>
                            <td>
                              <span className="badge badge-default font-weight-bold" style={{ backgroundColor: '#e0f2fe', color: '#0369a1', fontSize: '12px' }}>
                                {row.report_type}
                              </span>
                            </td>
                            <td>
                              <span className="text-capitalize">{row.report_category?.toLowerCase() || '-'}</span>
                            </td>
                            <td>
                              <strong>{row.report_format}</strong>
                            </td>
                            <td>
                              <span className="text-muted" style={{ fontSize: '12px' }}>{row.data_type}</span>
                            </td>
                            <td className="text-center">
                              {row.is_historical === 'TRUE' ? (
                                <span className="badge badge-warning" style={{ fontSize: '10px' }}>Yes</span>
                              ) : (
                                <span className="badge badge-default" style={{ fontSize: '10px' }}>No</span>
                              )}
                            </td>
                            <td className="text-center">
                              <span
                                className={`badge ${
                                  row.status === 'COMPLETED'
                                    ? 'badge-success'
                                    : row.status === 'FAILED'
                                    ? 'badge-danger'
                                    : 'badge-warning text-dark'
                                }`}
                                style={{ fontSize: '11px' }}
                              >
                                {row.status}
                              </span>
                            </td>
                            <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                              {row.created_on
                                ? new Date(row.created_on).toLocaleString([], {
                                    year: 'numeric',
                                    month: 'short',
                                    day: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                : '-'}
                            </td>
                            <td className="text-center">
                              <div className="cpo-table-actions justify-content-center">
                                <button
                                  type="button"
                                  className="btn btn-xs btn-primary font-weight-bold"
                                  onClick={() => setSelectedSavedReport(row)}
                                  title="View this saved report"
                                >
                                  <FontAwesomeIcon icon={faEye} className="me-1" /> View
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-xs btn-danger"
                                  onClick={(e) => handleDeleteSavedReport(row.id, e)}
                                  title="Delete report from database"
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination footer */}
                {!loadingSaved && savedTotal > savedLimit && (
                  <div className="p-3 bg-light border-top d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <span className="text-muted" style={{ fontSize: '12px' }}>
                      Showing {(savedPage - 1) * savedLimit + 1} to{' '}
                      {Math.min(savedPage * savedLimit, savedTotal)} of {savedTotal} saved records
                    </span>
                    <div className="btn-group btn-group-sm">
                      <button
                        type="button"
                        className="btn btn-default"
                        disabled={savedPage <= 1}
                        onClick={() => fetchSavedReportsList(savedPage - 1, savedSearch)}
                      >
                        Previous
                      </button>
                      <button type="button" className="btn btn-default active" disabled>
                        Page {savedPage} of {Math.ceil(savedTotal / savedLimit) || 1}
                      </button>
                      <button
                        type="button"
                        className="btn btn-default"
                        disabled={savedPage >= Math.ceil(savedTotal / savedLimit)}
                        onClick={() => fetchSavedReportsList(savedPage + 1, savedSearch)}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CareparrvaOutput;
